// Style plumbing for widget pop-out windows. The child window starts as a
// blank same-origin document, so every stylesheet and the theme attributes
// from the dashboard document must be copied across and kept in sync.

const isStyleNode = (node) =>
  node?.tagName === 'STYLE' || (node?.tagName === 'LINK' && node?.rel === 'stylesheet');

const copyStyleSheet = (styleSheet, targetDocument) => {
  const ownerNode = styleSheet.ownerNode;

  // Keep linked stylesheets linked. The browser can reuse its HTTP cache and
  // avoids synchronously serializing the application's full production CSSOM
  // on the dashboard thread for every pop-out.
  if (ownerNode?.tagName === 'LINK') {
    targetDocument.head.appendChild(ownerNode.cloneNode(true));
    return;
  }

  // STYLE nodes can contain CSSOM-only rules added through insertRule, so
  // serialize those rules rather than relying on the node's text content.
  const cssText = Array.from(styleSheet.cssRules)
    .map((rule) => rule.cssText)
    .join('\n');
  const styleNode = targetDocument.createElement('style');
  styleNode.textContent = cssText;
  targetDocument.head.appendChild(styleNode);
};

// Copies every stylesheet present right now. Linked application bundles stay
// as links; dynamically injected STYLE sheets are captured from CSSOM.
export const copyDocumentStyles = (sourceDocument, targetDocument) => {
  Array.from(sourceDocument.styleSheets).forEach((styleSheet) => {
    try {
      copyStyleSheet(styleSheet, targetDocument);
    } catch (error) {
      if (styleSheet.ownerNode) {
        targetDocument.head.appendChild(styleSheet.ownerNode.cloneNode(true));
      }
    }
  });
};

// Mirrors stylesheets added after the window opened (lazy-loaded feature CSS,
// emotion/MUI style tags injected on first render of a component).
export const createHeadStyleMirror = (sourceDocument, targetDocument) => {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (isStyleNode(node)) {
          targetDocument.head.appendChild(node.cloneNode(true));
        }
      });
    });
  });

  observer.observe(sourceDocument.head, { childList: true });
  return () => observer.disconnect();
};

const THEME_ATTRIBUTES = ['class', 'data-theme', 'data-accessibility-color-mode'];

const applyThemeAttributes = (sourceBody, targetBody) => {
  THEME_ATTRIBUTES.forEach((attributeName) => {
    const value = sourceBody.getAttribute(attributeName);
    if (value === null) {
      targetBody.removeAttribute(attributeName);
    } else {
      targetBody.setAttribute(attributeName, value);
    }
  });
};

// Keeps the child window's <body> theme attributes in lockstep with the
// dashboard, so theme/accessibility mode changes restyle the pop-out live.
export const createBodyThemeMirror = (sourceBody, targetBody) => {
  applyThemeAttributes(sourceBody, targetBody);

  const observer = new MutationObserver(() => applyThemeAttributes(sourceBody, targetBody));
  observer.observe(sourceBody, { attributes: true, attributeFilter: THEME_ATTRIBUTES });
  return () => observer.disconnect();
};
